import { useEffect } from 'react';
import { Mail, MapPin, Shield, Target, Users } from 'lucide-react';
import Footer from './Footer';

export default function ContactPage() {
  useEffect(() => {
    document.title = 'Contact Semando - Deploy Elite Tech Specialists';
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Contact Semando for elite tech specialists. Direct email: business@semando.us. Located in Sheridan, WY. Deploy The Architect, Perfectionist, or Watcher for critical systems.');
    }
  }, []);
  return (
    <div className="min-h-screen space-y-0">
      {/* Hero Section */}
      <section className="py-24 bg-gradient-to-br from-background to-muted/20 relative">
        <div className="absolute inset-0 cyber-grid opacity-20" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center space-y-8">
            <div className="space-y-4">
              <div className="inline-flex items-center px-4 py-2 bg-primary/10 rounded-full border border-primary/20">
                <Shield className="h-5 w-5 text-primary mr-2" />
                <span className="text-sm text-primary">SECURE_TRANSMISSION</span>
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl tracking-tight">
                <span className="block">Deploy Our Specialists</span>
                <span className="block text-primary">When Systems Matter</span>
              </h1>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto leading-relaxed">
                Critical situations demand immediate response. Our elite specialists are standing by, ready to architect, perfect, and protect your most vital systems.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* About Semando */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-16">
            <div className="text-center space-y-4">
              <h2 className="text-3xl md:text-4xl">About Semando</h2>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                We are not your typical consultancy. We are digital operatives who thrive in high-stakes environments where failure is not an option.
              </p>
            </div>
            
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              <div className="space-y-8">
                <div className="space-y-6">
                  <div className="space-y-3">
                    <h3 className="text-xl text-primary">Elite Operations</h3>
                    <p className="text-muted-foreground leading-relaxed">
                      Founded by former tech industry veterans who understood that traditional consulting wasn't built for the digital battlefield. We operate where others fear to tread—in the critical systems that power modern business.
                    </p>
                  </div>
                  
                  <div className="space-y-3">
                    <h3 className="text-xl text-primary">Specialist Network</h3>
                    <p className="text-muted-foreground leading-relaxed">
                      Our team consists of highly specialized operatives: The Architect designs intelligent futures, The Perfectionist ensures flawless execution, and The Watcher maintains eternal vigilance against digital threats.
                    </p>
                  </div>
                  
                  <div className="space-y-3">
                    <h3 className="text-xl text-primary">Mission Critical</h3>
                    <p className="text-muted-foreground leading-relaxed">
                      When reputation, revenue, and business continuity are on the line, organizations turn to us. We don't just deliver solutions—we forge digital armor that protects and empowers.
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="space-y-8">
                <div className="p-8 rounded-lg border border-primary/20 bg-primary/5 cyber-glow">
                  <div className="space-y-6">
                    <h3 className="text-xl">Why Choose Semando</h3>
                    
                    <div className="space-y-4">
                      <div className="flex items-start space-x-3">
                        <Shield className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                        <div className="space-y-1">
                          <h4 className="text-sm">Battle-Tested Experience</h4>
                          <p className="text-sm text-muted-foreground">Proven success in mission-critical environments across finance, healthcare, and enterprise systems.</p>
                        </div>
                      </div>
                      
                      <div className="flex items-start space-x-3">
                        <Target className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                        <div className="space-y-1">
                          <h4 className="text-sm">Precision Deployment</h4>
                          <p className="text-sm text-muted-foreground">Rapid response capabilities with specialists operational within hours of engagement.</p>
                        </div>
                      </div>
                      
                      <div className="flex items-start space-x-3">
                        <Users className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                        <div className="space-y-1">
                          <h4 className="text-sm">Elite Network</h4>
                          <p className="text-sm text-muted-foreground">Access to specialized expertise across AI, security, and quality assurance domains.</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Information - New Approach */}
      <section className="py-24 bg-gradient-to-r from-background via-primary/5 to-background relative">
        <div className="absolute inset-0 cyber-grid opacity-30" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center space-y-6 mb-16">
            <h2 className="text-4xl md:text-5xl">Get In Touch</h2>
            <p className="text-xl text-muted-foreground">
              Ready to deploy our specialists? Contact us directly.
            </p>
          </div>
          
          {/* Main Contact Section */}
          <div className="max-w-4xl mx-auto">
            <div className="bg-card border border-primary/20 rounded-lg p-6 sm:p-8 lg:p-12 cyber-glow cyber-box">
              <div className="grid lg:grid-cols-3 gap-8 sm:gap-12 items-start">
                {/* Contact Email */}
                <div className="lg:col-span-2 space-y-6">
                  <div className="flex flex-col sm:flex-row sm:items-center space-y-4 sm:space-y-0 sm:space-x-4">
                    <div className="flex items-center justify-center w-16 h-16 bg-primary/10 rounded-lg flex-shrink-0">
                      <Mail className="h-8 w-8 text-primary" />
                    </div>
                    <div className="space-y-2 min-w-0">
                      <h3 className="text-xl sm:text-2xl">Direct Contact</h3>
                      <a 
                        href="mailto:business@semando.us" 
                        className="text-xl sm:text-2xl lg:text-3xl text-primary hover:text-primary/80 transition-colors block break-all"
                        aria-label="Send email to business@semando.us"
                      >
                        business@semando.us
                      </a>
                    </div>
                  </div>
                  
                  <div className="space-y-3 text-muted-foreground">
                    <p>We don’t use contact forms. We don’t have a sales team. We’re a small, elite unit dedicated to solving genuinely hard problems, and we’re selective about the missions we accept.</p>
                  </div>
                </div>
                
                {/* Location */}
                <div className="space-y-6">
                  <div className="flex items-start space-x-4">
                    <div className="flex items-center justify-center w-12 h-12 bg-primary/10 rounded-lg flex-shrink-0">
                      <MapPin className="h-6 w-6 text-primary" />
                    </div>
                    <div className="space-y-2 min-w-0">
                      <h3 className="text-lg sm:text-xl">Operations Center</h3>
                      <div className="text-sm sm:text-base text-muted-foreground space-y-1">
                        <div>1309 Coffeen Avenue</div>
                        <div>Ste 1200</div>
                        <div>Sheridan, WY 82801</div>
                        <div>United States</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}